import React, { useState, useRef, useEffect } from 'react';
import { IntakeChat } from '../services/geminiService';
import { Message, EvidenceFile } from '../types';

const Report: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'model',
      content: "System Audit Intake Initialized. This is a Professional Disclosure Channel intended for the formal reporting of operational anomalies and governance breaches. Please describe the institutional failure or anomaly you wish to report.",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [evidence, setEvidence] = useState<EvidenceFile[]>([]);
  const [step, setStep] = useState<'chat' | 'review' | 'submitted'>('chat');
  const [caseId, setCaseId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInstance = useRef<IntakeChat | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Check for stored API key
    const storedKey = localStorage.getItem('gemini_api_key');
    if (storedKey) {
      setApiKey(storedKey);
    }
  }, []);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    if (!apiKey) {
      setShowApiKeyModal(true);
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      if (!chatInstance.current) {
        chatInstance.current = new IntakeChat(apiKey);
      }

      const responseText = await chatInstance.current.sendMessage(input);
      const modelMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'model',
        content: responseText,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, modelMessage]);
    } catch (err) {
      console.error(err);
      setMessages(prev => [...prev, {
        id: 'error',
        role: 'model',
        content: "Secure connection interrupted. Please check your API key configuration and retry.",
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const newEvidence: EvidenceFile = {
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          type: file.type,
          size: file.size,
          data: ev.target?.result as string,
          uploadedAt: new Date()
        };
        setEvidence(prev => [...prev, newEvidence]);
      };
      reader.readAsDataURL(file);
    });
  };

  const saveApiKey = () => {
    localStorage.setItem('gemini_api_key', apiKey);
    setShowApiKeyModal(false);
  };

  const finalizeSubmission = async () => {
    setIsLoading(true);
    setTimeout(() => {
      setCaseId(`SA-${Math.random().toString(36).toUpperCase().substr(2, 6)}-${new Date().getFullYear()}`);
      setStep('submitted');
      setIsLoading(false);
    }, 2000);
  };

  if (step === 'submitted') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-32 text-center">
        <div className="w-20 h-20 bg-black text-white rounded-none flex items-center justify-center mx-auto mb-12">
          <i className="fas fa-check text-4xl"></i>
        </div>
        <h1 className="text-5xl font-black mb-8 tracking-tighter uppercase">Submission Finalized</h1>
        <p className="text-xl text-slate-500 mb-12 max-w-2xl mx-auto font-medium">
          Integrity Pipeline data encrypted and stored. Forensic review in progress.
        </p>
        <div className="bg-white border border-slate-200 p-10 mb-12 inline-block text-left rounded-none">
          <div className="mb-8">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.3em]">Encrypted Case ID</span>
            <p className="text-3xl font-mono font-bold text-slate-900">{caseId}</p>
          </div>
          <div className="mb-8">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.3em]">Integrity Fingerprint</span>
            <p className="text-xs font-mono text-slate-400 break-all">SHA-256: f4d2e1...8a9c01b</p>
          </div>
          <button className="text-[10px] font-black uppercase tracking-[0.2em] border-b-2 border-black pb-1 hover:text-slate-500 hover:border-slate-500 transition-all">
            Download Forensic Receipt
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      {/* API Key Modal */}
      {showApiKeyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-black mb-4">Configure AI Intake</h3>
            <p className="text-sm text-slate-500 mb-6">Enter your Gemini API key to enable the AI-powered intake assistant.</p>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter Gemini API Key"
              className="w-full p-4 border border-slate-200 mb-4"
            />
            <div className="flex gap-4">
              <button
                onClick={saveApiKey}
                className="flex-1 py-3 bg-black text-white font-bold text-xs uppercase tracking-widest"
              >
                Save Key
              </button>
              <button
                onClick={() => setShowApiKeyModal(false)}
                className="flex-1 py-3 border border-slate-200 font-bold text-xs uppercase tracking-widest"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mb-16">
        <h1 className="text-5xl font-black text-slate-900 mb-6 tracking-tighter">Secure Data Submission:<br/><span className="text-slate-400">The Integrity Pipeline</span></h1>
        <p className="text-xl text-slate-500 font-medium leading-relaxed">
          This is a Professional Disclosure Channel intended for the formal reporting of operational anomalies and governance breaches.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Left Side: Conversation */}
        <div className="lg:col-span-2 flex flex-col h-[70vh] bg-white border border-slate-200 rounded-none overflow-hidden shadow-2xl">
          <div className="bg-black text-white p-5 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div>
              <h3 className="text-xs font-black uppercase tracking-[0.3em]">Encrypted Intake Session</h3>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowApiKeyModal(true)}
                className="text-[10px] text-slate-400 hover:text-white transition-colors"
              >
                <i className="fas fa-key mr-2"></i>Configure
              </button>
              <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">TLS 1.3 Active</div>
            </div>
          </div>
          
          <div className="flex-grow overflow-y-auto p-8 space-y-8 bg-slate-50/30">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-6 rounded-none border ${
                  m.role === 'user' ? 'bg-black text-white border-black shadow-lg' : 'bg-white text-slate-800 border-slate-200'
                }`}>
                  <p className="text-sm font-medium leading-relaxed whitespace-pre-wrap">{m.content}</p>
                  <span className="text-[9px] font-bold uppercase tracking-widest opacity-40 block mt-4 font-mono">
                    TIMESTAMP: {m.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 p-4 flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 bg-slate-900 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-slate-900 rounded-full animate-bounce" style={{ animationDelay: '75ms' }}></div>
                  <div className="w-1.5 h-1.5 bg-slate-900 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSendMessage} className="p-6 border-t border-slate-200 bg-white">
            <div className="flex space-x-3">
              <input 
                type="text" 
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Submit briefing content..."
                className="flex-grow p-4 bg-slate-50 border border-slate-200 rounded-none focus:outline-none focus:ring-1 focus:ring-black font-medium transition-all"
                disabled={isLoading}
              />
              <button 
                type="submit"
                disabled={isLoading || !input.trim()}
                className="px-8 bg-black text-white font-bold uppercase tracking-widest hover:bg-slate-800 disabled:opacity-50 transition-all"
              >
                Send
              </button>
            </div>
          </form>
        </div>

        {/* Right Side: Protocol Info */}
        <div className="space-y-8">
          <div className="bg-white border border-slate-200 p-8 rounded-none shadow-sm">
            <h3 className="text-xs font-black uppercase tracking-[0.3em] mb-6">Submission Protocol</h3>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-slate-100 flex items-center justify-center shrink-0">
                  <i className="fas fa-route text-slate-400 text-xs"></i>
                </div>
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest mb-1">In Transit</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">Utilization of TLS 1.3 with Perfect Forward Secrecy.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-slate-100 flex items-center justify-center shrink-0">
                  <i className="fas fa-vault text-slate-400 text-xs"></i>
                </div>
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest mb-1">At Rest</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">Data encrypted using military-grade AES-256-GCM.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-slate-100 flex items-center justify-center shrink-0">
                  <i className="fas fa-plug-circle-xmark text-slate-400 text-xs"></i>
                </div>
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest mb-1">Air-Gapped</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">Forensic audits conducted on systems physically isolated from the internet.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-slate-100 flex items-center justify-center shrink-0">
                  <i className="fas fa-eye-slash text-slate-400 text-xs"></i>
                </div>
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest mb-1">Zero-Knowledge</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">Administrators cannot access raw contents without contributor keys.</p>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-slate-100">
              <label className="block w-full cursor-pointer">
                <div className="border-2 border-dashed border-slate-200 rounded-none p-8 text-center hover:border-black transition-all group">
                  <i className="fas fa-folder-open text-3xl text-slate-300 group-hover:text-black mb-4 transition-colors"></i>
                  <p className="text-xs font-black uppercase tracking-widest">Attach Evidence</p>
                  <input type="file" className="hidden" multiple onChange={handleFileUpload} />
                </div>
              </label>
              {evidence.length > 0 && (
                <div className="mt-4 space-y-2">
                  {evidence.map(file => (
                    <div key={file.id} className="text-xs text-slate-500 flex items-center gap-2">
                      <i className="fas fa-file"></i>
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-black text-white p-8 rounded-none">
            <button 
              onClick={finalizeSubmission}
              disabled={messages.length < 3 || isLoading}
              className="w-full py-5 bg-white text-black font-black uppercase tracking-[0.2em] hover:bg-slate-200 disabled:opacity-50 transition-all flex items-center justify-center space-x-3 text-xs"
            >
              <i className="fas fa-lock"></i>
              <span>Finalize & Submit</span>
            </button>
            <p className="text-[9px] text-slate-500 mt-4 uppercase text-center font-bold tracking-[0.1em]">By submitting, you agree to the forensic disclosure protocol.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Report;
